import React, { useState, useEffect } from 'react';
import ExpenseAnalyzerPage from './ExpenseAnalyzerPage';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle, labelStyle, tdStyle, thStyle, statCard } from '../theme';

// `analyzer` legacy hub key opens ExpensesPage on the Analyzer tab.
const LEGACY_TAB_MAP = { 'analyzer': 'analyzer' };

const ExpensesPage = ({ API_BASE, initialTab = 'list' }) => {
  const [tab, setTab] = useState(LEGACY_TAB_MAP[initialTab] || initialTab);
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pnl, setPnl] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [dateTo, setDateTo]     = useState(new Date().toISOString().split('T')[0]);
  const [form, setForm] = useState({ categoryId:'', vendor:'', description:'', amount:'', mode:'cash', date:new Date().toISOString().split('T')[0], notes:'' });
  const [editing, setEditing] = useState(null);
  const [newCat, setNewCat] = useState('');

  const fetchAll = async () => {
    try {
      const [e, c, p] = await Promise.all([
        axios.get(`${API_BASE}/expenses?from=${dateFrom}&to=${dateTo}`),
        axios.get(`${API_BASE}/expenses/categories`),
        axios.get(`${API_BASE}/expenses/pnl?from=${dateFrom}&to=${dateTo}`),
      ]);
      setExpenses(e.data); setCategories(c.data); setPnl(p.data);
    } catch (e) {}
  };

  useEffect(() => { fetchAll(); }, [dateFrom, dateTo]); // eslint-disable-line

  const handleSave = async () => {
    if (!form.amount) return alert('Amount is required');
    try {
      if (editing) await axios.put(`${API_BASE}/expenses/${editing._id}`, { ...form, amount: Number(form.amount) });
      else         await axios.post(`${API_BASE}/expenses`, { ...form, amount: Number(form.amount) });
      setForm({ categoryId:'', vendor:'', description:'', amount:'', mode:'cash', date:new Date().toISOString().split('T')[0], notes:'' });
      setEditing(null); setTab('list'); fetchAll();
    } catch (e) { alert('Save failed'); }
  };

  const handleEdit = (exp) => {
    setEditing(exp);
    setForm({
      categoryId: exp.categoryId?._id || exp.categoryId || '',
      vendor: exp.vendor||'', description: exp.description||'',
      amount: exp.amount||'', mode: exp.mode||'cash',
      date: exp.date ? new Date(exp.date).toISOString().split('T')[0] : '',
      notes: exp.notes||'',
    });
    setTab('add');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    await axios.delete(`${API_BASE}/expenses/${id}`);
    fetchAll();
  };

  const handleAddCategory = async () => {
    if (!newCat.trim()) return;
    await axios.post(`${API_BASE}/expenses/categories`, { name: newCat.trim() });
    setNewCat('');
    fetchAll();
  };

  const fmtINR  = n => `₹${(n||0).toLocaleString('en-IN')}`;
  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '—';

  const modeLabels = { cash:'Cash', card:'Card', upi:'UPI', cheque:'Cheque', 'bank-transfer':'Bank', other:'Other' };

  const tabBtn = (key, label) => (
    <button onClick={()=>setTab(key)}
      style={{padding:'9px 20px', borderRadius:'8px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem',
        backgroundColor: tab===key ? theme.primary : '#e9ecef', color: tab===key ? 'white' : '#555'}}>
      {label}
    </button>
  );

  const totalExpense = expenses.reduce((s, e) => s + (e.amount||0), 0);

  return (
    <div>
      {/* P&L KPI */}
      {pnl && (
        <div style={{display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'14px', marginBottom:'20px'}}>
          <div style={statCard}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Revenue (Billed)</span>
            <h2 style={{margin:'4px 0 0', color:theme.info, fontSize:'1.3rem', fontWeight:'700'}}>{fmtINR(pnl.revenue)}</h2>
          </div>
          <div style={statCard}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Collected</span>
            <h2 style={{margin:'4px 0 0', color:theme.success, fontSize:'1.3rem', fontWeight:'700'}}>{fmtINR(pnl.collected)}</h2>
          </div>
          <div style={statCard}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Expenses</span>
            <h2 style={{margin:'4px 0 0', color:theme.danger, fontSize:'1.3rem', fontWeight:'700'}}>{fmtINR(pnl.expenses)}</h2>
          </div>
          <div style={{...statCard, borderLeft:`4px solid ${pnl.profit >= 0 ? theme.success : theme.danger}`}}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Net Profit</span>
            <h2 style={{margin:'4px 0 0', color: pnl.profit >= 0 ? theme.success : theme.danger, fontSize:'1.3rem', fontWeight:'700'}}>{fmtINR(pnl.profit)}</h2>
          </div>
          <div style={statCard}>
            <span style={{fontSize:'0.78rem', color:theme.textMuted, fontWeight:'600', textTransform:'uppercase'}}>Expense Count</span>
            <h2 style={{margin:'4px 0 0', color:theme.navy, fontSize:'1.3rem', fontWeight:'700'}}>{pnl.expenseCount}</h2>
          </div>
        </div>
      )}

      {/* Date filter + tabs */}
      <div style={{display:'flex', gap:'10px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center'}}>
        {tabBtn('list', 'Expenses')}
        {tabBtn('add', editing ? 'Edit Expense' : '+ Add Expense')}
        {tabBtn('pnl', 'Profit & Loss')}
        {tabBtn('categories', 'Categories')}
        {tabBtn('analyzer', 'Analyzer')}
        <div style={{marginLeft:'auto', display:'flex', gap:'8px', alignItems:'center'}}>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)} style={{...inputStyle, width:'auto', padding:'8px 12px'}}/>
          <span style={{color:theme.textMuted}}>to</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)} style={{...inputStyle, width:'auto', padding:'8px 12px'}}/>
        </div>
      </div>

      {/* ANALYZER — ExpenseAnalyzerPage embedded as a tab */}
      {tab === 'analyzer' && <ExpenseAnalyzerPage API_BASE={API_BASE} />}

      {/* Add/Edit */}
      {tab === 'add' && (
        <div style={{...cardStyle, marginBottom:'20px', borderTop:`4px solid ${theme.success}`}}>
          <h3 style={{margin:'0 0 16px', color:theme.navy}}>{editing ? 'Edit Expense' : 'Add Expense'}</h3>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr', gap:'12px', marginBottom:'12px'}}>
            <div>
              <label style={labelStyle}>Category</label>
              <select value={form.categoryId} onChange={e=>setForm({...form, categoryId:e.target.value})} style={inputStyle}>
                <option value="">— Select —</option>
                {categories.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div><label style={labelStyle}>Vendor</label><input value={form.vendor} onChange={e=>setForm({...form, vendor:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Amount ₹ *</label><input type="number" value={form.amount} onChange={e=>setForm({...form, amount:e.target.value})} style={inputStyle}/></div>
            <div>
              <label style={labelStyle}>Mode</label>
              <select value={form.mode} onChange={e=>setForm({...form, mode:e.target.value})} style={inputStyle}>
                {Object.entries(modeLabels).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div style={{gridColumn:'span 2'}}><label style={labelStyle}>Description</label><input value={form.description} onChange={e=>setForm({...form, description:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Date</label><input type="date" value={form.date} onChange={e=>setForm({...form, date:e.target.value})} style={inputStyle}/></div>
            <div><label style={labelStyle}>Notes</label><input value={form.notes} onChange={e=>setForm({...form, notes:e.target.value})} style={inputStyle}/></div>
          </div>
          <div style={{display:'flex', gap:'10px'}}>
            <button onClick={handleSave} style={{...btnStyle, marginTop:0}}>{editing ? 'Update' : 'Add Expense'}</button>
            {editing && <button onClick={()=>{setEditing(null); setTab('list');}} style={{...btnStyle, marginTop:0, backgroundColor:'#6c757d'}}>Cancel</button>}
          </div>
        </div>
      )}

      {/* Expense List */}
      {tab === 'list' && (
        <div style={cardStyle}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px'}}>
            <h3 style={{margin:0, color:theme.navy}}>Expenses ({expenses.length})</h3>
            <strong style={{color:theme.danger, fontSize:'1.05rem'}}>Total: {fmtINR(totalExpense)}</strong>
          </div>
          {expenses.length === 0 ? (
            <div style={{textAlign:'center', padding:'50px', color:'#bbb'}}>No expenses in selected range</div>
          ) : (
            <table style={{width:'100%', borderCollapse:'collapse'}}>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Category</th>
                  <th style={thStyle}>Vendor</th>
                  <th style={thStyle}>Description</th>
                  <th style={thStyle}>Mode</th>
                  <th style={{...thStyle, textAlign:'right'}}>Amount</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {expenses.map(e => (
                  <tr key={e._id}>
                    <td style={tdStyle}>{fmtDate(e.date)}</td>
                    <td style={tdStyle}>{e.categoryId?.name || '—'}</td>
                    <td style={tdStyle}>{e.vendor || '—'}</td>
                    <td style={tdStyle}>{e.description || '—'}</td>
                    <td style={tdStyle}>{modeLabels[e.mode] || e.mode}</td>
                    <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.danger}}>{fmtINR(e.amount)}</td>
                    <td style={tdStyle}>
                      <div style={{display:'flex', gap:'6px'}}>
                        <button onClick={()=>handleEdit(e)} style={{padding:'4px 10px', backgroundColor:'#0582ca', color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Edit</button>
                        <button onClick={()=>handleDelete(e._id)} style={{padding:'4px 10px', backgroundColor:theme.danger, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.74rem', fontWeight:'600'}}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* P&L Report */}
      {tab === 'pnl' && pnl && (
        <div style={cardStyle}>
          <h3 style={{margin:'0 0 18px', color:theme.navy}}>Profit & Loss Statement</h3>
          <table style={{width:'100%', maxWidth:'600px', borderCollapse:'collapse', marginBottom:'24px'}}>
            <tbody>
              <tr style={{borderBottom:`1px solid ${theme.border}`}}>
                <td style={{...tdStyle, fontWeight:'600'}}>Total Billed (Revenue)</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.info}}>{fmtINR(pnl.revenue)}</td>
              </tr>
              <tr style={{borderBottom:`1px solid ${theme.border}`}}>
                <td style={{...tdStyle, fontWeight:'600'}}>Total Collected</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.success}}>{fmtINR(pnl.collected)}</td>
              </tr>
              <tr style={{borderBottom:`1px solid ${theme.border}`}}>
                <td style={{...tdStyle, fontWeight:'600'}}>Outstanding (Billed - Collected)</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.warning}}>{fmtINR(pnl.revenue - pnl.collected)}</td>
              </tr>
              <tr style={{borderBottom:`2px solid ${theme.border}`}}>
                <td style={{...tdStyle, fontWeight:'600'}}>Total Expenses</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', color:theme.danger}}>{fmtINR(pnl.expenses)}</td>
              </tr>
              <tr style={{backgroundColor: pnl.profit >= 0 ? '#f0fdf4' : '#fef2f2'}}>
                <td style={{...tdStyle, fontWeight:'700', fontSize:'1.05rem'}}>Net Profit (Collected - Expenses)</td>
                <td style={{...tdStyle, textAlign:'right', fontWeight:'700', fontSize:'1.15rem', color: pnl.profit >= 0 ? theme.success : theme.danger}}>{fmtINR(pnl.profit)}</td>
              </tr>
            </tbody>
          </table>

          {pnl.expenseByCategory.length > 0 && (
            <>
              <h4 style={{color:theme.navy, margin:'0 0 12px'}}>Expense Breakdown by Category</h4>
              <table style={{width:'100%', maxWidth:'500px', borderCollapse:'collapse'}}>
                <thead><tr><th style={thStyle}>Category</th><th style={{...thStyle, textAlign:'right'}}>Amount</th><th style={{...thStyle, textAlign:'right'}}>%</th></tr></thead>
                <tbody>
                  {pnl.expenseByCategory.map(c => (
                    <tr key={c.name}>
                      <td style={tdStyle}>{c.name}</td>
                      <td style={{...tdStyle, textAlign:'right', fontWeight:'600'}}>{fmtINR(c.amount)}</td>
                      <td style={{...tdStyle, textAlign:'right', color:theme.textMuted}}>{pnl.expenses > 0 ? Math.round((c.amount / pnl.expenses) * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Categories */}
      {tab === 'categories' && (
        <div style={cardStyle}>
          <h3 style={{margin:'0 0 14px', color:theme.navy}}>Expense Categories</h3>
          <div style={{display:'flex', gap:'10px', marginBottom:'16px'}}>
            <input value={newCat} onChange={e=>setNewCat(e.target.value)} placeholder="New category name..." style={{...inputStyle, maxWidth:'300px'}}
              onKeyDown={e=>{ if (e.key === 'Enter') handleAddCategory(); }}/>
            <button onClick={handleAddCategory} style={{...btnStyle, marginTop:0}}>+ Add</button>
          </div>
          <div style={{display:'flex', flexWrap:'wrap', gap:'8px'}}>
            {categories.map(c => (
              <span key={c._id} style={{padding:'6px 14px', backgroundColor:'#f8f9fa', borderRadius:'20px', fontSize:'0.85rem', border:'1px solid #dee2e6', color:theme.navy, fontWeight:'500'}}>
                {c.name}
              </span>
            ))}
            {categories.length === 0 && <span style={{color:'#aaa'}}>No categories yet</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
